// PostPaymentWelcome — Sprint 4 of the IA correction (spec §8).
// Shown right after a successful payment and before any program-specific
// assessment modules begin. Lives at /app/assessment/:sessionId/welcome.
//
// Two entry points:
//   • Path B (choice-led) — Plan tab "PAY & BEGIN ASSESSMENT" → Stripe
//     checkout → redirect here on success. Has a program list in the
//     session (selectedPrograms) and a per-program fee ($49 + $19 each).
//   • Path A (assessment-led) — Discovery recommendation "CONTINUE TO
//     PAYMENT" → Stripe → redirect here on success. NO program list —
//     the physician decides post-assessment. Flat $75.
//
// Backend coordination: the route accepts `?paid=1` query string after a
// successful Stripe redirect, but the frontend doesn't gate on it (the
// session document is the truth — if the session has `paymentReceived` or
// `feeReceipt` we trust it). When backend hasn't yet written
// selectedPrograms (race after Stripe redirect), we briefly retry.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import { apiGet } from "@/lib/api";
import { useApplicationTray } from "@/lib/applicationTray";
import { useAuth } from "@/lib/auth";
import { C } from "@/lib/brand";
import { fmtMoney, titleCase } from "@/lib/format";

const ASSESSMENT_LED_FEE_CENTS = 7500;
const CHOICE_LED_BASE_CENTS = 4900;
const CHOICE_LED_ADDL_CENTS = 1900;

export default function PostPaymentWelcome() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { consumer } = useAuth();
  const [session, setSession] = useState(null);
  const { items: trayItems, clear: clearTray } = useApplicationTray();

  // Re-fetch the session up to 3 times if selectedPrograms hasn't been
  // written yet — Stripe's webhook → BE → FE redirect can race the
  // session update by a few hundred ms. Cap the retry so a genuinely-
  // empty assessment-led session doesn't loop forever.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempt = 0;
    const fetchOnce = async () => {
      const s = await apiGet(`/consumer/assessment/sessions/${sessionId}`).catch(() => null);
      if (cancelled) return;
      setSession(s);
      // Only retry if it's a choice-led session that hasn't materialized
      // its program list yet. Assessment-led sessions intentionally have
      // no selectedPrograms; retrying them would be wasted work.
      const isChoiceLed = s && (s.pathType === "choice_led" || (Array.isArray(s.selectedPrograms) && s.selectedPrograms.length > 0));
      const needsRetry = isChoiceLed && (!Array.isArray(s.selectedPrograms) || s.selectedPrograms.length === 0);
      if (needsRetry && attempt < 2) {
        attempt += 1;
        setTimeout(fetchOnce, 800);
      }
    };
    fetchOnce();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!session) {
    return <div style={{ padding: 64, textAlign: "center", background: C.cream, minHeight: "100vh" }}><Spinner /></div>;
  }

  const isAssessmentLed = session.pathType === "assessment_led";

  // Programs covered by this payment — prefer the session's authoritative
  // selectedPrograms list. The backend has shipped a few different shapes
  // for this field over time (array of objects with various key names,
  // array of slug strings, array of program IDs), so we normalize each
  // entry and backfill from the tray when names are missing.
  //
  // Assessment-led sessions intentionally have NO program list — the
  // physician picks the program post-assessment — so we skip the
  // resolution entirely for that path.
  const trayBySlug = Object.fromEntries(
    trayItems
      .filter((t) => t?.programSlug)
      .map((t) => [t.programSlug, t]),
  );
  const trayByProgramId = Object.fromEntries(
    trayItems
      .filter((t) => t?.programId)
      .map((t) => [t.programId, t]),
  );
  const programs = isAssessmentLed ? [] : (() => {
    const raw = Array.isArray(session.selectedPrograms) ? session.selectedPrograms : [];
    if (raw.length === 0) {
      return trayItems.map((it) => ({
        slug: it.programSlug,
        name: it.programName,
        tierName: it.tierName,
      }));
    }
    return raw
      .map((p) => {
        if (typeof p === "string") {
          // Could be a slug or a program id — match either against the tray.
          const t = trayBySlug[p] || trayByProgramId[p];
          return {
            slug: t?.programSlug || p,
            name: t?.programName || "",
            tierName: t?.tierName || "",
          };
        }
        const slug = p.slug || p.programSlug;
        const fallback = trayBySlug[slug] || trayByProgramId[p.programId || p.id] || {};
        return {
          slug,
          name: p.name || p.programName || fallback.programName || "",
          tierName: p.tierName || p.tierLabel || p.tier || fallback.tierName || "",
        };
      })
      .filter((p) => p.name); // hide entries we couldn't resolve a name for
  })();

  // Fee comes back from the backend in CENTS (Stripe convention). Divide
  // before fmtMoney — matches the FeeSummary fix from earlier.
  // Default amounts depend on path:
  //   • assessment-led — flat $75
  //   • choice-led — $49 base + $19 per additional program (cents)
  // We default to the path-correct fee only when the BE hasn't yet
  // populated feeReceipt/amountPaid (Stripe race after redirect).
  const choiceLedDefault = programs.length === 0 ? 0 : (CHOICE_LED_BASE_CENTS + Math.max(programs.length - 1, 0) * CHOICE_LED_ADDL_CENTS);
  const feeCents = session.feeReceipt?.total
    ?? session.amountPaid
    ?? (isAssessmentLed ? ASSESSMENT_LED_FEE_CENTS : choiceLedDefault);
  const feeDollars = feeCents / 100;
  const firstName = titleCase(consumer?.firstName || "there");

  const begin = () => {
    // The tray's job is done — selections have moved into the session.
    // Clear it so the home/program-detail/Plan banners don't double-count
    // selections that are now in flight as paid commitments.
    clearTray();
    navigate(`/app/assessment/${sessionId}`);
  };

  return (
    <div data-testid="post-payment-welcome" style={{ minHeight: "100vh", background: C.cream, paddingBottom: 80 }}>
      <header style={{ background: C.ink, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Logo light size={20} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>★ NEXT: ASSESSMENT</span>
      </header>

      <section style={{ padding: "72px 24px 32px", maxWidth: 720, margin: "0 auto" }}>
        <Eyebrow color={C.peachDeep} testId="welcome-eyebrow">★ PAYMENT RECEIVED</Eyebrow>
        <Headline
          before="THANK YOU,"
          italic={`${firstName.toLowerCase()}.`}
          size={68}
          className="mt-6"
        />
      </section>

      <section style={{ padding: "0 24px", maxWidth: 720, margin: "0 auto" }}>
        <div style={{ background: C.peach, color: C.peachDark, padding: "26px 24px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>
            {isAssessmentLed
              ? `★ YOUR ${fmtMoney(feeDollars).toUpperCase()} CONSULTATION COVERS`
              : `★ YOUR ${fmtMoney(feeDollars).toUpperCase()} COVERS PHYSICIAN REVIEW OF`}
          </div>
          {isAssessmentLed ? (
            // Assessment-led — no program list. Instead, show what's
            // included so the receipt feels meaningful even though no
            // program has been chosen yet.
            <ul data-testid="welcome-includes-list" style={{ marginTop: 14, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                ["A 12-minute health assessment", "Designed by physicians, not a quiz"],
                ["Board-certified physician review", "Of your complete responses"],
                ["Personalized protocol recommendation", "The program (or programs) most likely to fit"],
                ["Follow-up messaging in your dashboard", "If your physician needs more details"],
              ].map(([title, sub]) => (
                <li key={title} style={{ display: "flex", gap: 10, fontSize: 16, color: C.ink, lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 800, color: C.peachDark }}>•</span>
                  <span>
                    <strong style={{ fontWeight: 800 }}>{title}</strong>
                    <span style={{ color: C.peachDark }}> — {sub}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <ul data-testid="welcome-program-list" style={{ marginTop: 14, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {programs.length === 0 ? (
                <li style={{ fontSize: 14, color: C.peachDark, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
                  We're still finalizing your receipt — it'll appear in your dashboard within a minute.
                </li>
              ) : (
                programs.map((p) => (
                  <li key={p.slug} data-testid={`welcome-program-${p.slug}`} style={{ display: "flex", gap: 10, fontSize: 16, color: C.ink, lineHeight: 1.45 }}>
                    <span style={{ fontWeight: 800, color: C.peachDark }}>•</span>
                    <span>
                      <strong style={{ fontWeight: 800 }}>{p.name}</strong>
                      {p.tierName && <span style={{ color: C.peachDark }}> — {p.tierName} tier</span>}
                    </span>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <p style={{ marginTop: 32, fontSize: 17, lineHeight: 1.7, color: C.ink, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          Now we just need to know about you. The assessment takes about 12 minutes — it's how your physician designs the right protocol for you.
        </p>
        <p style={{ marginTop: 14, fontSize: 14, color: C.warmGray }}>
          You can save and resume anytime in your dashboard.
        </p>

        <div style={{ marginTop: 36 }}>
          <Button variant="primary" size="lg" testId="welcome-begin-btn" onClick={begin}>
            BEGIN ASSESSMENT →
          </Button>
        </div>
      </section>
    </div>
  );
}
